import { useState, useCallback, useRef, useEffect } from 'react';
import { useArt } from '../../../backend/useArt';
import { useArt3D } from '../../../backend/useArt3D';
import { ArtType } from '../Art';

export interface Point {
    x: number;
    y: number;
    pressure?: number;
}

export interface TransformState {
    x: number;
    y: number;
    rotation: number;
    scaleX: number;
    scaleY: number;
}

export type ObjectTypes = 'stroke' | 'image';

export interface Object {
    type: ObjectTypes;
    data: ObjectData;
    transform: TransformState;
}

export interface ObjectData {
    points: Point[];
    thickness: number;
    color: string;
    imageData?: string; // Base64 data URL for images
    isFill?: boolean; // Flag to indicate if this is a fill shape
}

export interface HistoryEntry {
    createdAt: string;
    updatedAt: string;
    iterationId: string; // This will be the DB entry id
    objects: Object[];
    userPng: string;
    description: string;
    aiImage: string;
    ai3dModel?: string; // URL to 3D model generated from the AI image
}

export interface UseArtHistoryReturn {
    history: HistoryEntry[];
    currentIndex: number;
    setCurrentIndex: (index: number) => void;
    addHistoryEntry: (entry: Omit<HistoryEntry, 'iterationId' | 'createdAt' | 'updatedAt'>) => HistoryEntry;
    updateEntry: (index: number, updates: Partial<HistoryEntry>) => void;
    undo: () => HistoryEntry | undefined;
    redo: () => HistoryEntry | undefined;
    clearHistory: () => void;
    canUndo: boolean;
    canRedo: boolean;
}

interface UseArtHistoryProps {
    artId?: string;
    artType: ArtType;
}

export function useArtHistory({ artId, artType }: UseArtHistoryProps): UseArtHistoryReturn {
    const [history, setHistory] = useState<HistoryEntry[]>([]);
    const [currentIndex, setCurrentIndex] = useState(-1);
    const [isInitialized, setIsInitialized] = useState(false);
    const { createHistoryEntry, updateHistoryEntry, getArt } = useArt();
    const { createHistoryEntry3D, updateHistoryEntry3D, getArt3D } = useArt3D();
    const isUpdatingFromDBRef = useRef(false);

    // Initialize history from the database
    useEffect(() => {
        if (!artId || isInitialized) return;

        const fetchHistoryFromDB = async () => {
            try {
                isUpdatingFromDBRef.current = true;
                let artWithHistory;
                
                if (artType === '3D') {
                    artWithHistory = await getArt3D(artId);
                } else {
                    artWithHistory = await getArt(artId);
                }
                
                const historyEntriesArray = artType === '3D' 
                    ? artWithHistory?.art_3d_history_entries 
                    : artWithHistory?.art_history_entries;
                
                if (artWithHistory && historyEntriesArray) {
                    // Convert DB entries to our HistoryEntry format
                    const historyEntries = historyEntriesArray.map(entry => ({
                        createdAt: entry.created_at,
                        updatedAt: entry.updated_at,
                        iterationId: entry.iteration_id,
                        objects: entry.objects,
                        userPng: entry.user_png,
                        description: entry.description,
                        aiImage: entry.ai_image,
                        ai3dModel: artType === '3D' ? entry.ai_3d_model : undefined,
                    }));
                    
                    if (historyEntries.length > 0) {
                        setHistory(historyEntries);
                        setCurrentIndex(historyEntries.length - 1);
                    }
                }
                
                setIsInitialized(true);
                isUpdatingFromDBRef.current = false;
            } catch (error) {
                console.error('Error fetching history from DB:', error);
                setIsInitialized(true);
                isUpdatingFromDBRef.current = false;
            }
        };

        fetchHistoryFromDB();
    }, [artId, getArt, getArt3D, artType, isInitialized]);

    // Add a new history entry
    const addHistoryEntry = useCallback((entry: Omit<HistoryEntry, 'iterationId' | 'createdAt' | 'updatedAt'>) => {
        const now = new Date().toISOString();
        
        // Ensure objects is always a properly copied array
        const objectsCopy = Array.isArray(entry.objects) 
            ? entry.objects.map(obj => ({...obj}))  // Deep copy each object
            : [];
        
        // Create a temporary entry with a placeholder iterationId
        const tempEntry: HistoryEntry = {
            ...entry,
            objects: objectsCopy,
            iterationId: 'temp-' + now,
            createdAt: now,
            updatedAt: now,
        };

        // console.trace('TRACE Adding history entry:', tempEntry);

        // console.log('Adding history entry to artId:', artId);
        // If we have an artId, save to DB
        if (artId && !isUpdatingFromDBRef.current) {
            // Add to history immediately with temp entry
            setHistory(prev => [...prev, tempEntry]);
            setCurrentIndex(prev => prev === -1 ? 0 : history.length);
            
            // Then update asynchronously when DB operation completes
            if (artType === '3D') {
                createHistoryEntry3D(
                    artId,
                    entry.description || '',
                    entry.userPng || '',
                    entry.aiImage || '',
                    entry.ai3dModel || '',
                    objectsCopy || []
                ).then(dbEntry => {
                    // Update the entry with the DB-generated ID and timestamps
                    setHistory(prev => {
                        const newHistory = [...prev];
                        const lastIndex = newHistory.length - 1;
                        
                        // Replace the temp entry with the one from DB
                        if (lastIndex >= 0 && newHistory[lastIndex].iterationId === tempEntry.iterationId) {
                            newHistory[lastIndex] = {
                                ...entry,
                                objects: objectsCopy,
                                iterationId: dbEntry.iteration_id,
                                createdAt: dbEntry.created_at,
                                updatedAt: dbEntry.updated_at,
                                ai3dModel: dbEntry.ai_3d_model,
                            };
                        }
                        
                        return newHistory;
                    });
                }).catch(error => {
                    console.error('Error adding 3D history entry to DB:', error);
                    // Entry is already in local state with temp ID, so no further action needed
                });
            } else {
                createHistoryEntry(
                    artId,
                    entry.description || '',
                    entry.userPng || '',
                    entry.aiImage || '',
                    objectsCopy || []
                ).then(dbEntry => {
                    // console.log('Added history entry to artId:', artId);
                    // Update the entry with the DB-generated ID and timestamps
                    setHistory(prev => {
                        const newHistory = [...prev];
                        const lastIndex = newHistory.length - 1;
                        
                        // Replace the temp entry with the one from DB
                        if (lastIndex >= 0 && newHistory[lastIndex].iterationId === tempEntry.iterationId) {
                            newHistory[lastIndex] = {
                                ...entry,
                                objects: objectsCopy,
                                iterationId: dbEntry.iteration_id,
                                createdAt: dbEntry.created_at,
                                updatedAt: dbEntry.updated_at,
                            };
                        }
                        
                        return newHistory;
                    });
                }).catch(error => {
                    console.error('Error adding history entry to DB:', error);
                    // Entry is already in local state with temp ID, so no further action needed
                });
            }
        }
        
        return tempEntry;
    }, [artId, createHistoryEntry, createHistoryEntry3D, artType, history.length, isUpdatingFromDBRef.current]);

    // Update entry at specific index
    const updateEntry = useCallback((index: number, updates: Partial<HistoryEntry>) => {
        if (isUpdatingFromDBRef.current) return;

        setHistory(prev => {
            if (prev.length === 0 || index < 0 || index >= prev.length) return prev;

            const updatedHistory = [...prev];
            const entryToUpdate = updatedHistory[index];

            // console.trace('TRACE Updating history entry:', updates, 'original:', entryToUpdate);
            
            // Ensure objects is always an array when updating
            const updatedObjects = updates.objects !== undefined 
                ? (Array.isArray(updates.objects) ? [...updates.objects] : []) 
                : entryToUpdate.objects;
            
            const now = new Date().toISOString();
            
            // Create updated entry
            const updatedEntry = {
                ...entryToUpdate,
                ...updates,
                objects: updatedObjects,
                updatedAt: now
            };
            
            updatedHistory[index] = updatedEntry;
            
            if (index <= 0 || entryToUpdate.iterationId.startsWith('temp-')) {
                const updatedEntryWithoutIterationId = {
                    ...updatedEntry,
                    iterationId: undefined
                };
                // console.log('Adding history entry:', updatedEntryWithoutIterationId);
                addHistoryEntry(updatedEntryWithoutIterationId);
            } else if (artId && entryToUpdate.iterationId) {
                // Prepare update data for DB
                const dbUpdates: any = {};
                
                if (updates.description !== undefined) dbUpdates.description = updates.description;
                if (updates.userPng !== undefined) dbUpdates.user_png = updates.userPng;
                if (updates.aiImage !== undefined) dbUpdates.ai_image = updates.aiImage;
                if (updates.objects !== undefined) dbUpdates.objects = updatedObjects;
                
                // For 3D art, also handle the ai3dModel field
                if (artType === '3D' && updates.ai3dModel !== undefined) {
                    dbUpdates.ai_3d_model = updates.ai3dModel;
                }
                
                // Only update if there's something to update
                if (Object.keys(dbUpdates).length > 0) {
                    if (artType === '3D') {
                        updateHistoryEntry3D(entryToUpdate.iterationId, dbUpdates)
                            .catch(error => console.error('Error updating 3D history entry in DB:', error));
                    } else {
                        updateHistoryEntry(entryToUpdate.iterationId, dbUpdates)
                            .catch(error => console.error('Error updating history entry in DB:', error));
                    }
                }
            }
            
            return updatedHistory;
        });
    }, [artId, updateHistoryEntry]);

    // Undo - go back one step
    const undo = useCallback(() => {
        if (history.length === 0) return undefined;
        
        const newIndex = currentIndex > 0 ? currentIndex - 1 : 0;
        setCurrentIndex(newIndex);
        return history[newIndex];
    }, [currentIndex, history]);

    // Redo - go forward one step
    const redo = useCallback(() => {
        if (history.length === 0 || currentIndex >= history.length - 1) return undefined;
        
        const newIndex = currentIndex + 1;
        setCurrentIndex(newIndex);
        return history[newIndex];
    }, [currentIndex, history]);

    // Clear all history (local only - doesn't delete from DB)
    const clearHistory = useCallback(() => {
        setHistory([]);
        setCurrentIndex(-1);
    }, []);

    return {
        history,
        currentIndex,
        setCurrentIndex,
        addHistoryEntry,
        updateEntry,
        undo,
        redo,
        clearHistory,
        canUndo: currentIndex > 0 || (currentIndex === -1 && history.length > 0),
        canRedo: currentIndex < history.length - 1,
    };
}
